import { LiveHubService } from './livehub.service';
import { Title } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import { ssApiService } from './ss-api.service';
import { Subject } from 'rxjs/Subject';
import { SalesRepresentativeArray } from '../models/domain/sales.representative';
import { ProductItemBasicsArray } from '../models/domain/product.item.basics';
import { AdminItemArray, AdminItem } from '../models/domain/admin.item';
import { CustomTicketWidgetItem } from '../models/domain/custom.ticket.widget';
import { Router } from '@angular/router';
import { UserRoleItem, UserRoleTypes } from '../models/domain/user.roles';
import { IBaseResponse } from '../models/comm-http/responses/BaseResponse';
import { environment } from '../../environments/environment';
import { AdvancedSearchResultMemberItemArray } from '../models/domain/advanced.searche.results';
import * as moment from 'moment';
import { DialogService } from './modal-dialogs/dialog.service';
import { GetSalesRepresentativesRequest } from '@models/ss-api/requests/get.sales.representatives.request';
import { LoginResponse } from '@models/ss-api/responses/login.response';

export enum AppStateTypes {
    Online,
    TestMode
}

@Injectable()
export class GlobalService {

    AdminHistoryMaxResults = 10;
    SearchMaxResults = 100;

    HttpTimeout: number = 20000;

    SalesRepresentatives = new SalesRepresentativeArray();
    StructureStudiosProductsBasics = new ProductItemBasicsArray();

    TwoFactorQR: string;
    TwoFactorManualEntryKey: string;
    DeviceID: string;
    AdminId: number;
    Username: string;
    FirstName: string;
    LastName: string;
    FullName: string;
    Email: string;
    
    Gravatar: string = '';

    Token: string;
    TokenExpiration: Date;
    DateTimeLogin: Date;

    UserRole: UserRoleItem = new UserRoleItem;

    LastMemberID: number;
    LastMemberFullName: string;

    MemberPanelURL: string =  environment.memberUrl;
    BaseApiURL: string = environment.baseUrl;
    Version: string = environment.version;

    AllAdmins: AdminItemArray;

    DraggedTicket: CustomTicketWidgetItem;

    // Dev variables from environment settings
    envName: string = environment.envName;
    isDevMode: boolean;

    OutOfDate: boolean = false;

    // SessionExpirationObservable

    public onAdvancedSearchClick = new Subject<IBaseResponse<Boolean>>();
    public onSearchResults = new Subject<IBaseResponse<AdvancedSearchResultMemberItemArray>>();

    constructor(private api: ssApiService, private router: Router, private titleService: Title, public liveHub: LiveHubService) {
        this.ReadFromStorage();
        this.CreateSubscriptions();
        this.SessionExpireCheck();
        this.GetGravatar();
        this.CheckDevMode();
        this.LoadSignature();

        this.MemberPanelURL =  this.api.GetMemberURL();
        this.BaseApiURL = this.api.GetBaseURL();
        let disposable = this.api.onUnauthorized.subscribe(response => {
            disposable.unsubscribe();
            this.Logout();
        })
    }


    SessionExpireCheck() {
        setInterval(() => {
            let TokenExpiration = localStorage.getItem('crm_token_expiration');
            if (TokenExpiration && new Date(TokenExpiration) < new Date())
                console.log("Session is expired"); // this.Logout();
        }, 5000);
    }

    CheckDevMode() {
        let mode = localStorage.getItem('ss-dev-mode');
        if (mode == 'true') {
            this.isDevMode = true;
        } else {
            this.isDevMode = false;
        }
    }

    LoadSignature() {
        let id = localStorage.getItem('crm_AdminID');

        if (id) {
            this.GetAdmins(true).then(response => {
                let currentAdmin = response.filter(admin => {
                    return admin.AdminID == Number(id);
                })[0]

                localStorage.setItem('crm_HTMLEmailSignature', currentAdmin.HTMLEmailSignature);
            });
        }

    }

    CreateSubscriptions() {

        // list of representatives
        this.api.onSalesRepresentatives.subscribe(response => {
            if (response.Result) {
                // console.log("global services sales reps intercepted:", response.Data)
                this.SalesRepresentatives = response.Data;
            }
        })

        // list of structure studios products basics
        this.api.onProductStructureStudiosProductsBasics.subscribe(response => {
            if (response.Result) {
                // console.log("products intercepted:", response.Data)
                this.StructureStudiosProductsBasics = response.Data;
            }
        })

        // admins
        this.api.onAdminAllAdmins.subscribe(response => {
            if (response.Result) {
                // console.log("Admins Intercepted:", response.Data)
                this.AllAdmins = response.Data;
            }
        })

    }

    SetPageTitle(title: string) {
        let version = this.api.GetBaseURL().includes('dev') ? '[Dev] ' : '';
        this.titleService.setTitle(version + title);
    }

    async GetAdmins(refresh: boolean = false): Promise<AdminItemArray> {

        if (refresh ||
            this.AllAdmins == undefined ||
            this.AllAdmins.length < 1) {
            await this.api.getAdmins().toPromise();
        }

        return this.AllAdmins;
    }

    async GetSalesRepresentatives(refresh: boolean = false, activeonly: boolean = false, currentSalesRepID: number = null): Promise<SalesRepresentativeArray> {

        if (refresh || this.SalesRepresentatives == undefined || this.SalesRepresentatives.length < 1) {
            var request = new GetSalesRepresentativesRequest();
            request.ActiveOnly = activeonly;
            request.CurrentSalesRepID = currentSalesRepID;
            await this.api.GetSalesRepresentatives(request).toPromise();
        }

        return this.SalesRepresentatives;
    }

    async GetStructureStudiosProductsBasics(refresh: boolean = false): Promise<ProductItemBasicsArray> {

        if (refresh ||
            this.StructureStudiosProductsBasics == undefined ||
            this.StructureStudiosProductsBasics.length < 1) {
            await this.api.GetStructureStudiosProductsBasics().toPromise();
        }

        // Simple Option But Not Ordered
        // this.StructureStudiosProductsBasics.sort((left, right) : number => {
        //     if (left.ID == 5 || left.ID == 8 || left.ID == 9 || left.ID == 22) return -1;
        //     return 1;
        // })


        // Complicated But Ordered
        let staticProducts = [];
        let remainingProducts = [];

        this.StructureStudiosProductsBasics.forEach(function(product) {
            switch (product.ID) {
                case 1:
                case 2:
                case 3:
                case 5:
                    staticProducts.push(product);
                    break;
                default:
                    remainingProducts.push(product);
                    break;
            }
        })

        staticProducts.sort((left, right): number => {
            if (left.ID < right.ID) return -1;
            return 1;
        })

        remainingProducts.sort((left, right): number => {
            if (left.Name < right.Name) return -1;
            return 1;
        })

        staticProducts = staticProducts.concat(remainingProducts);

        return staticProducts;
    }

    ReadFromStorage() {

        let DeviceID = localStorage.getItem('DeviceID');
        if (DeviceID != undefined)
            this.DeviceID = DeviceID;

        let AdminId = localStorage.getItem('crm_AdminID');
        if (AdminId != undefined)
            this.AdminId = <any>AdminId;

        let Username = localStorage.getItem('crm_Username');
        if (Username != undefined)
            this.Username = Username;

        let FirstName = localStorage.getItem('crm_FirstName');
        if (FirstName != undefined)
            this.FirstName = FirstName;

        let LastName = localStorage.getItem('crm_LastName');
        if (LastName != undefined)
            this.LastName = LastName;

        let Email = localStorage.getItem('crm_Email');
        if (Email != undefined)
            this.Email = Email;


        let Token = localStorage.getItem('crm_token');
        if (Token != undefined)
            this.Token = Token;

        let TokenExpiration = localStorage.getItem('crm_token_expiration');
        if (TokenExpiration != undefined)
            this.TokenExpiration = new Date(TokenExpiration);

        let DateTimeLogin = localStorage.getItem('crm_DateTimeLogin');
        if (DateTimeLogin != undefined)
            this.DateTimeLogin = new Date(DateTimeLogin);


        this.FullName = this.FirstName + ' ' + this.LastName;
    }

    UpdateAdmin(admin: AdminItem) {
        this.GetAdmins(true);
        this.LoadSignature();

        localStorage.setItem('crm_AdminID', admin.AdminID.toString());
        localStorage.setItem('crm_Username', admin.Username);
        localStorage.setItem('crm_FirstName', admin.FirstName);
        localStorage.setItem('crm_LastName', admin.LastName);
        localStorage.setItem('crm_Email', admin.Email);
    }

    public SetGlobalsFromLoginResponse(data: LoginResponse) {
        this.DeviceID = data.DeviceID;
        this.AdminId = data.AdminId;
        this.DateTimeLogin = new Date();
        this.Email = data.Email;
        this.Username = data.Username;
        this.FirstName = data.FirstName;
        this.LastName = data.LastName;
        this.Token = data.Token;
        this.TokenExpiration = data.TokenExpiration;
        this.TwoFactorQR = data.TwoFactorQR;
        this.TwoFactorManualEntryKey = data.TwoFactorManualEntryKey;

        localStorage.setItem('crm_AdminID', data.AdminId.toString());
        localStorage.setItem('crm_DateTimeLogin', new Date().toString());
        localStorage.setItem('crm_Email', data.Email);
        localStorage.setItem('crm_Username', data.Username);
        localStorage.setItem('crm_FirstName', data.FirstName);
        localStorage.setItem('crm_LastName', data.LastName);

        localStorage.setItem('crm_token', data.Token);
        localStorage.setItem('crm_token_expiration', data.TokenExpiration.toString());

        localStorage.setItem('DeviceID', data.DeviceID);
       
    }

    getGuid() {
        return GlobalService.GetGuid();
    }

    public static GetGuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    public Logout() {

        localStorage.setItem("is_authenticated", "0");
        localStorage.removeItem("crm_token");
        localStorage.removeItem("crm_token_expiration");
        localStorage.removeItem("crm_DateTimeLogin");

        this.BaseApiURL = this.api.GetBaseURL();
        console.log("lr3");
        this.router.navigate(['/authentication']);
    }

    public SetupUserRole() {

        this.UserRole.Role = UserRoleTypes.TechSupport;


        if (this.Email == undefined)
            return;

        if (this.Email.toLowerCase() == 'nickolas@structurestudios.com' || this.Email.toLowerCase() == 'fausto@structurestudios.com')
            this.UserRole.Role = UserRoleTypes.Developer;
        else if (this.Email.toLowerCase() == 'stefanie@structurestudios.com' || this.Email.toLowerCase() == 'stefanie@structure.com' || this.Email.toLowerCase() == 'james@structurestudios.com' || this.Email.toLowerCase() == 'james@structure.com' || this.Email.toLowerCase() == 'whitney@structurestudios.com')
            this.UserRole.Role = UserRoleTypes.Administrator;

    }

    public AdvancedSearchClick() {
        this.onAdvancedSearchClick.next();
    }

    public OpenSearchResults() {
        this.onSearchResults.next();
    }

    public replaceAll(str, find, replace) {
        return str.replace(new RegExp(this.escapeRegExp(find), 'g'), replace);
    }

    public escapeRegExp(str) {
        return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
    }

    public ConvertToUrlFriendly(input: string): string {
        // this.Token = this.Token.Replace("\\", "_");
        input = this.replaceAll(input, '/', '~');
        input = this.replaceAll(input, '+', '|');
        input = this.replaceAll(input, '=', '<');

        return input;
    }


    GetGravatar() {

        if (this.Email == undefined)
            return;

        let GravatarEmail = this.Email;
        GravatarEmail = GravatarEmail.trim(); // "MyEmailAddress@example.com"
        GravatarEmail = GravatarEmail.toLowerCase(); // "myemailaddress@example.com"

        let MD5 = function (s) { function L(k, d) { return (k << d) | (k >>> (32 - d)) } function K(G, k) { let I, d, F, H, x; F = (G & 2147483648); H = (k & 2147483648); I = (G & 1073741824); d = (k & 1073741824); x = (G & 1073741823) + (k & 1073741823); if (I & d) { return (x ^ 2147483648 ^ F ^ H) } if (I | d) { if (x & 1073741824) { return (x ^ 3221225472 ^ F ^ H) } else { return (x ^ 1073741824 ^ F ^ H) } } else { return (x ^ F ^ H) } } function r(d, F, k) { return (d & F) | ((~d) & k) } function q(d, F, k) { return (d & k) | (F & (~k)) } function p(d, F, k) { return (d ^ F ^ k) } function n(d, F, k) { return (F ^ (d | (~k))) } function u(G, F, aa, Z, k, H, I) { G = K(G, K(K(r(F, aa, Z), k), I)); return K(L(G, H), F) } function f(G, F, aa, Z, k, H, I) { G = K(G, K(K(q(F, aa, Z), k), I)); return K(L(G, H), F) } function D(G, F, aa, Z, k, H, I) { G = K(G, K(K(p(F, aa, Z), k), I)); return K(L(G, H), F) } function t(G, F, aa, Z, k, H, I) { G = K(G, K(K(n(F, aa, Z), k), I)); return K(L(G, H), F) } function e(G) { let Z; let F = G.length; let x = F + 8; let k = (x - (x % 64)) / 64; let I = (k + 1) * 16; let aa = Array(I - 1); let d = 0; let H = 0; while (H < F) { Z = (H - (H % 4)) / 4; d = (H % 4) * 8; aa[Z] = (aa[Z] | (G.charCodeAt(H) << d)); H++ } Z = (H - (H % 4)) / 4; d = (H % 4) * 8; aa[Z] = aa[Z] | (128 << d); aa[I - 2] = F << 3; aa[I - 1] = F >>> 29; return aa } function B(x) { let k = '', F = '', G, d; for (d = 0; d <= 3; d++) { G = (x >>> (d * 8)) & 255; F = '0' + G.toString(16); k = k + F.substr(F.length - 2, 2) } return k } function J(k) { k = k.replace(/rn/g, 'n'); let d = ''; for (let F = 0; F < k.length; F++) { let x = k.charCodeAt(F); if (x < 128) { d += String.fromCharCode(x) } else { if ((x > 127) && (x < 2048)) { d += String.fromCharCode((x >> 6) | 192); d += String.fromCharCode((x & 63) | 128) } else { d += String.fromCharCode((x >> 12) | 224); d += String.fromCharCode(((x >> 6) & 63) | 128); d += String.fromCharCode((x & 63) | 128) } } } return d } let C = Array(); let P, h, E, v, g, Y, X, W, V; let S = 7, Q = 12, N = 17, M = 22; let A = 5, z = 9, y = 14, w = 20; let o = 4, m = 11, l = 16, j = 23; let U = 6, T = 10, R = 15, O = 21; s = J(s); C = e(s); Y = 1732584193; X = 4023233417; W = 2562383102; V = 271733878; for (P = 0; P < C.length; P += 16) { h = Y; E = X; v = W; g = V; Y = u(Y, X, W, V, C[P + 0], S, 3614090360); V = u(V, Y, X, W, C[P + 1], Q, 3905402710); W = u(W, V, Y, X, C[P + 2], N, 606105819); X = u(X, W, V, Y, C[P + 3], M, 3250441966); Y = u(Y, X, W, V, C[P + 4], S, 4118548399); V = u(V, Y, X, W, C[P + 5], Q, 1200080426); W = u(W, V, Y, X, C[P + 6], N, 2821735955); X = u(X, W, V, Y, C[P + 7], M, 4249261313); Y = u(Y, X, W, V, C[P + 8], S, 1770035416); V = u(V, Y, X, W, C[P + 9], Q, 2336552879); W = u(W, V, Y, X, C[P + 10], N, 4294925233); X = u(X, W, V, Y, C[P + 11], M, 2304563134); Y = u(Y, X, W, V, C[P + 12], S, 1804603682); V = u(V, Y, X, W, C[P + 13], Q, 4254626195); W = u(W, V, Y, X, C[P + 14], N, 2792965006); X = u(X, W, V, Y, C[P + 15], M, 1236535329); Y = f(Y, X, W, V, C[P + 1], A, 4129170786); V = f(V, Y, X, W, C[P + 6], z, 3225465664); W = f(W, V, Y, X, C[P + 11], y, 643717713); X = f(X, W, V, Y, C[P + 0], w, 3921069994); Y = f(Y, X, W, V, C[P + 5], A, 3593408605); V = f(V, Y, X, W, C[P + 10], z, 38016083); W = f(W, V, Y, X, C[P + 15], y, 3634488961); X = f(X, W, V, Y, C[P + 4], w, 3889429448); Y = f(Y, X, W, V, C[P + 9], A, 568446438); V = f(V, Y, X, W, C[P + 14], z, 3275163606); W = f(W, V, Y, X, C[P + 3], y, 4107603335); X = f(X, W, V, Y, C[P + 8], w, 1163531501); Y = f(Y, X, W, V, C[P + 13], A, 2850285829); V = f(V, Y, X, W, C[P + 2], z, 4243563512); W = f(W, V, Y, X, C[P + 7], y, 1735328473); X = f(X, W, V, Y, C[P + 12], w, 2368359562); Y = D(Y, X, W, V, C[P + 5], o, 4294588738); V = D(V, Y, X, W, C[P + 8], m, 2272392833); W = D(W, V, Y, X, C[P + 11], l, 1839030562); X = D(X, W, V, Y, C[P + 14], j, 4259657740); Y = D(Y, X, W, V, C[P + 1], o, 2763975236); V = D(V, Y, X, W, C[P + 4], m, 1272893353); W = D(W, V, Y, X, C[P + 7], l, 4139469664); X = D(X, W, V, Y, C[P + 10], j, 3200236656); Y = D(Y, X, W, V, C[P + 13], o, 681279174); V = D(V, Y, X, W, C[P + 0], m, 3936430074); W = D(W, V, Y, X, C[P + 3], l, 3572445317); X = D(X, W, V, Y, C[P + 6], j, 76029189); Y = D(Y, X, W, V, C[P + 9], o, 3654602809); V = D(V, Y, X, W, C[P + 12], m, 3873151461); W = D(W, V, Y, X, C[P + 15], l, 530742520); X = D(X, W, V, Y, C[P + 2], j, 3299628645); Y = t(Y, X, W, V, C[P + 0], U, 4096336452); V = t(V, Y, X, W, C[P + 7], T, 1126891415); W = t(W, V, Y, X, C[P + 14], R, 2878612391); X = t(X, W, V, Y, C[P + 5], O, 4237533241); Y = t(Y, X, W, V, C[P + 12], U, 1700485571); V = t(V, Y, X, W, C[P + 3], T, 2399980690); W = t(W, V, Y, X, C[P + 10], R, 4293915773); X = t(X, W, V, Y, C[P + 1], O, 2240044497); Y = t(Y, X, W, V, C[P + 8], U, 1873313359); V = t(V, Y, X, W, C[P + 15], T, 4264355552); W = t(W, V, Y, X, C[P + 6], R, 2734768916); X = t(X, W, V, Y, C[P + 13], O, 1309151649); Y = t(Y, X, W, V, C[P + 4], U, 4149444226); V = t(V, Y, X, W, C[P + 11], T, 3174756917); W = t(W, V, Y, X, C[P + 2], R, 718787259); X = t(X, W, V, Y, C[P + 9], O, 3951481745); Y = K(Y, h); X = K(X, E); W = K(W, v); V = K(V, g) } let i = B(Y) + B(X) + B(W) + B(V); return i.toLowerCase() };

        // April 1st (only)
        let todaysDate = new Date();
        if (todaysDate.getDate() == 1 && todaysDate.getMonth() == 3)
            GravatarEmail = 'nickolas@structurestudios.com';

        this.Gravatar = 'https://www.gravatar.com/avatar/' + MD5(GravatarEmail) + '.jpg?s=200';
        // console.log("GRAVATAR : " + this.Gravatar);
    }

}
